import {
  DocumentData, Query, DocumentReference, QuerySnapshot, DocumentSnapshot,
  getDocs, getDoc, getDocsFromCache, getDocFromCache,
} from 'firebase/firestore';

/**
 * Wraps a Firestore promise with a timeout.
 * Shows a friendly offline message if navigator.onLine is false.
 */
export const withTimeout = <T>(promise: Promise<T>, ms = 20000): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(
        navigator.onLine
          ? `Request timed out after ${ms / 1000}s. Check your connection and try again.`
          : 'You appear to be offline. Please check your connection.'
      )), ms)
    ),
  ]);

/**
 * Retry an async read a few times with linear backoff. For on-mount Firestore loads on a freshly
 * installed field tablet: the first read must reach the server (nothing is cached yet) and a flaky
 * connection can miss or time out, so a single attempt surfaces a spurious error. Callers still gate
 * the first call on auth being ready; this only covers the transient network/timeout miss after.
 */
export async function retry<T>(fn: () => Promise<T>, attempts = 3, baseDelayMs = 800): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      if (i < attempts - 1) await new Promise(r => setTimeout(r, baseDelayMs * (i + 1)));
    }
  }
  throw lastErr;
}

/* ── Cache-first reads for on-mount navigation ──
   The default getDocs is server-first: online, even with a warm local cache, it waits on a server
   round-trip (capped by withTimeout, 20 s) — so on a weak field connection every navigation stalled
   on the network, and retry backoff stacked more waiting on top. These helpers return the locally
   cached result at once (a warm cache = instant navigation, no round-trip), then refresh from the
   server in the background and hand the fresh result to onRefresh. When nothing is cached yet
   (fresh install), they fall back to a retryable server read. Only the first paint comes from
   cache; writes and background freshness are unaffected. Shared by every firebase/* read module. */
export async function queryCacheFirst<T>(
  q: Query<DocumentData>, map: (s: QuerySnapshot<DocumentData>) => T, onRefresh?: (v: T) => void,
): Promise<T> {
  try {
    const cached = await getDocsFromCache(q);   // resolves empty (not reject) on a cold query
    if (!cached.empty) {
      if (onRefresh) getDocs(q).then(s => onRefresh(map(s))).catch(() => { /* stay on cached */ });
      return map(cached);
    }
  } catch { /* nothing usable in cache — fall through to the server */ }
  return map(await retry(() => withTimeout(getDocs(q))));
}

export async function docCacheFirst<T>(
  ref: DocumentReference<DocumentData>, map: (s: DocumentSnapshot<DocumentData>) => T, onRefresh?: (v: T) => void,
): Promise<T> {
  try {
    const cached = await getDocFromCache(ref);   // rejects when the doc isn't cached
    if (cached.exists()) {
      if (onRefresh) getDoc(ref).then(s => onRefresh(map(s))).catch(() => { /* stay on cached */ });
      return map(cached);
    }
  } catch { /* not in cache — fall through to the server */ }
  return map(await retry(() => withTimeout(getDoc(ref))));
}

/**
 * Strips undefined values from an object before writing to Firestore.
 * Firestore rejects documents containing undefined field values.
 */
export const stripUndefined = (obj: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/** Convenience type alias for raw Firestore document data */
export type FirestoreData = DocumentData;
